import { Type } from '@sinclair/typebox';
// M27 — web-research provider store contracts (ADM-15; HUB-084 §M27). API-key
// material (plaintext OR ciphertext) is deliberately ABSENT from every response
// schema — it never leaves the DB row; only a `hasApiKey` presence boolean is
// exposed.
/**
 * The single runtime home of the 3 web capabilities. The TypeBox union below,
 * the per-capability provider store, and every web tool's provider resolution
 * consume THIS map — there is no second list anywhere. A typo against a member
 * fails type-check.
 */
export const WEB_CAPABILITIES = {
    search: 'search',
    extract: 'extract',
    nav: 'nav',
};
export const WebCapabilitySchema = Type.Union([
    Type.Literal(WEB_CAPABILITIES.search),
    Type.Literal(WEB_CAPABILITIES.extract),
    Type.Literal(WEB_CAPABILITIES.nav),
]);
/**
 * The single runtime home of the vendor ids each capability has an integration
 * for. The hub's provider registry is typed against THIS map (a vendor listed
 * here with no factory fails type-check) and the admin UI renders its provider
 * dropdown from it, so an operator can only pick an id the hub can actually
 * resolve — `provider` is stored verbatim and looked up by exact match, and an
 * unknown id fails closed as `web_capability_disabled`. The in-repo `stub`
 * vendor is deliberately absent: it exists only for tests, not for operators.
 */
export const WEB_PROVIDER_VENDORS = {
    search: ['exa'],
    extract: ['firecrawl'],
    nav: ['browserbase'],
};
/** Optional provider config (Browserbase optional `projectId`). */
export const WebProviderConfigSchema = Type.Object({
    projectId: Type.Optional(Type.String()),
});
/** Configured web provider (response) — carries only `hasApiKey`, never the key. */
export const WebProviderSchema = Type.Object({
    id: Type.String({ format: 'uuid' }),
    capability: WebCapabilitySchema,
    provider: Type.String(),
    hasApiKey: Type.Boolean(),
    enabled: Type.Boolean(),
    config: Type.Union([WebProviderConfigSchema, Type.Null()]),
    createdAt: Type.String({ format: 'date-time' }),
    updatedAt: Type.String({ format: 'date-time' }),
});
export const WebProviderCreateBody = Type.Object({
    capability: WebCapabilitySchema,
    provider: Type.String({ minLength: 1 }),
    apiKey: Type.String({ minLength: 1 }),
    enabled: Type.Optional(Type.Boolean()),
    config: Type.Optional(Type.Union([WebProviderConfigSchema, Type.Null()])),
});
export const WebProviderUpdateBody = Type.Object({
    provider: Type.Optional(Type.String({ minLength: 1 })),
    apiKey: Type.Optional(Type.String({ minLength: 1 })),
    enabled: Type.Optional(Type.Boolean()),
    config: Type.Optional(Type.Union([WebProviderConfigSchema, Type.Null()])),
});
/**
 * M27 task-06 — idempotent full-state upsert body for
 * `PUT /web-providers/:capability` (capability comes from the path). Mirrors the
 * `IdentityProviderPutBody` shape: `provider` is required, `enabled` defaults
 * off, `config` is the optional Browserbase `projectId` bag. `apiKey` is
 * OPTIONAL because it is write-only: the admin card never gets it back, so a
 * re-save that only flips `enabled` carries none — omitting it keeps the stored
 * ciphertext. The first write for a capability must supply one (400
 * `api_key_required`).
 */
export const WebProviderPutBody = Type.Object({
    provider: Type.String({ minLength: 1 }),
    apiKey: Type.Optional(Type.String({ minLength: 1 })),
    enabled: Type.Optional(Type.Boolean()),
    config: Type.Optional(Type.Union([WebProviderConfigSchema, Type.Null()])),
});
/** `:capability` path param for the per-capability web-provider routes. */
export const WebProviderCapabilityParamsSchema = Type.Object({
    capability: WebCapabilitySchema,
});
/** `GET /web-providers` — the full per-capability provider list (no keys). */
export const WebProviderListResponseSchema = Type.Array(WebProviderSchema);
//# sourceMappingURL=web-providers.js.map